import type { AxiosInstance } from 'axios';
import qs from 'query-string';
import type { WorkspaceObjects as WS } from './interfaces/workspace.object.js';
import { DownloadObjectRecursive } from './interfaces/workspace.object.js';
import { normalizeWorkspaceObject, transformDates } from './transformers/index.js';
import { normalizeWorkspace } from './transformers/workspaces.workspace.js';

export class WorkspacesObjectClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  /**
   * The users an object can be shared with, or its ownership given to: in an application workspace only those who
   * hold its application, elsewhere everyone in the account.
   */
  getShareableUsers = (data: WS.GetShareableUsers.Request): Promise<WS.GetShareableUsers.Response> => {
    const query = qs.stringify(
      { excluded: data.excluded, size: data.size, value: data.value },
      { arrayFormat: 'none', skipNull: true, skipEmptyString: true }
    );

    return this.authenticatedClient
      .get<WS.GetShareableUsers.RawResponse>(
        `/workspaces/${data.workspaceId}/objects/${data.objectId}/shareable-users?${query}`
      )
      .then((res) => ({
        items: res.data.items.map((user) => ({
          email: user.email,
          firstName: user.firstName ?? '',
          id: user.id,
          lastName: user.lastName ?? '',
        })),
      }));
  };

  /**
   * The teams an object can be shared with: those of the account that hold the application of the workspace, whether or not the
   * session user is in them.
   */
  getShareableTeams = (data: WS.GetShareableTeams.Request): Promise<WS.GetShareableTeams.Response> => {
    return this.authenticatedClient
      .get<WS.GetShareableTeams.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/shareable-teams`)
      .then((res) => res.data);
  };

  /** The people who are in a team the object is shared with now, a page at a time. */
  listAccessTeamMembers = (data: WS.ListAccessTeamMembers.Request): Promise<WS.ListAccessTeamMembers.Response> => {
    return this.authenticatedClient
      .get<WS.ListAccessTeamMembers.Response>(
        `/workspaces/${data.workspaceId}/objects/${data.objectId}/access/teams/${encodeURIComponent(data.teamIdentifier)}/members`,
        { params: { search: data.search || undefined, take: data.take, skip: data.skip } }
      )
      .then((res) => res.data);
  };

  getObjectAccess = ({ objectId, workspaceId }: WS.GetObjectAccess.Request) => {
    return this.authenticatedClient.get<WS.GetObjectAccess.Response>(`/workspaces/${workspaceId}/objects/${objectId}/access`)
      .then(res => ({
        items: res.data.items,
        teams: res.data.teams,
        object: normalizeWorkspaceObject(res.data.object),
        workspace: normalizeWorkspace(res.data.workspace),
      }));
  };

  moveObject = (data: WS.MoveObject.Request) => {
    return this.authenticatedClient.post<WS.MoveObject.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/move`, {
      parentObjectId: data.parentObjectId,
    }).then(res => ({
      object: normalizeWorkspaceObject(res.data.object),
    }));
  };

  moveObjectDetails = (data: WS.GetMoveObjectDetails.Request) => {
    return this.authenticatedClient.get<WS.GetMoveObjectDetails.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/move`, {
      transformResponse: transformDates,
    }).then(res => res.data);
  };

  saveObjectAccess = (data: WS.SaveObjectAccess.Request) => {
    return this.authenticatedClient.post(`/workspaces/${data.workspaceId}/objects/${data.objectId}/access`, {
      items: data.items,
      ...(data.teams === undefined ? {} : { teams: data.teams }),
    });
  };

  updateObjectExpiration = (data: WS.UpdateObjectExpiration.Request) => {
    return this.authenticatedClient.put<WS.UpdateObjectExpiration.Response<string>>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/expiration`, {
      expiresOn: data.expiresOn,
    }).then(res => ({
      object: normalizeWorkspaceObject(res.data.object),
    }));
  };

  updateObjectOwnership = (data: WS.UpdateObjectOwnership.Request) => {
    return this.authenticatedClient.post<WS.UpdateObjectOwnership.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/owner`, {
      ownerId: data.ownerId,
    }).then(res => ({
      object: normalizeWorkspaceObject(res.data.object),
    }));
  };

  getObjectChildren = (data: WS.GetObjectChildren.Request): Promise<WS.GetObjectChildren.Response> => {
    const query = qs.stringify({
      filters: JSON.stringify(data.filters || {}),
    }, { arrayFormat: 'bracket' });
    return this.authenticatedClient.get(`/workspaces/${data.workspaceId}/objects/${data.parentObjectId}/children?${query}`, {
      transformResponse: transformDates,
    });
  };

  downloadObjectRecursive = async ({ workspaceId, objectId, ...data }: DownloadObjectRecursive.Params & DownloadObjectRecursive.Body) => {
    const response = await this.authenticatedClient.post(`/workspaces/${workspaceId}/objects/${objectId}/download/recursive`, {
      objectIds: data.objectIds,
    });

    return DownloadObjectRecursive.responseSchema.parse(response.data);
  };

  toggleBookmark = async (data: WS.ToggleBookmark.Request): Promise<WS.ToggleBookmark.Response> => {
    return this.authenticatedClient.patch<WS.ToggleBookmark.Response>(`/workspaces/${data.workspaceId}/objects/${data.objectId}/bookmark`)
      .then(res => ({
        bookmarked: res.data.bookmarked,
      }));
  };
}

export default WorkspacesObjectClient;